/**
 * Manages recently viewed products.
 */
export class RecentlyViewed {
  /** @type {string} */
  static #STORAGE_KEY = 'recentlyViewedProducts';

  /** @type {number} */
  static #MAX_PRODUCTS = 16;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  static #syncTimeout;

  /** @type {string} */
  static #lastSyncedValue = '';

  /**
   * Adds a product to the recently viewed list.
   * @param {string} productHandle
   */
  static addProduct(productHandle) {
    const viewedProducts = this.getProducts();

    // Already the most recently viewed product.
    if (viewedProducts[0] === productHandle) {
      return;
    }

    const updatedProducts = [
      productHandle,
      ...viewedProducts.filter(handle => handle !== productHandle),
    ].slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(
      this.#STORAGE_KEY,
      JSON.stringify(updatedProducts)
    );

    this.#scheduleSync(updatedProducts);
  }

  /**
   * Clears recently viewed products.
   */
  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);

    this.#lastSyncedValue = '';

    clearTimeout(this.#syncTimeout);

    void this.syncCart([]);
  }

  /**
   * Gets recently viewed products.
   * @returns {string[]}
   */
  static getProducts() {
    try {
      return JSON.parse(
        localStorage.getItem(this.#STORAGE_KEY) ?? '[]'
      );
    } catch {
      return [];
    }
  }

  /**
   * Immediately flushes any pending sync.
   */
  static flush() {
    clearTimeout(this.#syncTimeout);
    void this.syncCart();
  }

  /**
   * Debounces syncing to Shopify.
   * @param {string[]} handles
   */
  static #scheduleSync(handles) {
    clearTimeout(this.#syncTimeout);

    this.#syncTimeout = setTimeout(() => {
      void this.syncCart(handles);
    }, 1000);
  }

  /**
   * Syncs recently viewed products to the cart attributes.
   * @param {string[]} handles
   */
  static async syncCart(handles = this.getProducts()) {
    const value = handles.join(',');

    // Skip identical updates.
    if (value === this.#lastSyncedValue) {
      return;
    }

    this.#lastSyncedValue = value;

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            recently_viewed: value,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      // Allow retry on the next update.
      this.#lastSyncedValue = '';

      console.error('Failed to sync recently viewed products:', error);
    }
  }
}

// Flush pending changes before the page is unloaded.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    RecentlyViewed.flush();
  }
});